/**
 * One place in a guide where a picture belongs.
 *
 * A seeded article ships the HOLES rather than the pictures: the screenshots are
 * of our own live systems, they change, and a guide written in the repo cannot
 * carry them. So the slot draws itself as an empty frame with the caption
 * saying what the picture has to show, and anybody who may edit fills it from
 * the media library.
 *
 * The empty state is deliberately loud. A missing screenshot that looks like
 * nothing at all is a guide that quietly reads as finished while the one thing
 * a colleague needed - what the screen actually looks like - is absent.
 */
import { Camera, ImageOff, RefreshCw } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { KbShot } from "../../../lib/kb";
import { Btn, IconBtn } from "../../ui";

export function ShotSlot({
  slotKey,
  caption,
  shot,
  canEdit,
  onPick,
  onClear,
}: {
  slotKey: string;
  caption: string;
  shot?: KbShot;
  canEdit: boolean;
  onPick: (key: string) => void;
  onClear: (key: string) => void;
}) {
  const t = useT();
  const label = shot?.caption || caption;

  if (!shot) {
    return (
      <figure className="crm-doc-slot" data-slot={slotKey}>
        <div className="crm-doc-slot-mark">
          <Camera className="size-5" strokeWidth={1.7} />
        </div>
        <figcaption>
          <span className="crm-doc-slot-kicker">{t("kb.shot.missing")}</span>
          <span className="crm-doc-slot-cap">{caption}</span>
        </figcaption>
        {canEdit ? (
          <Btn small onClick={() => onPick(slotKey)}>
            {t("kb.shot.add")}
          </Btn>
        ) : null}
      </figure>
    );
  }

  return (
    <figure className="crm-doc-shot" data-slot={slotKey}>
      {shot.kind === "video" ? (
        <video src={shot.url} controls preload="metadata" />
      ) : (
        <img src={shot.url} alt={label} loading="lazy" />
      )}
      <figcaption>
        <span>{label}</span>
        {canEdit ? (
          <span className="crm-doc-shot-act">
            <IconBtn small title={t("kb.shot.replace")} onClick={() => onPick(slotKey)}>
              <RefreshCw className="size-3.5" />
            </IconBtn>
            <IconBtn small title={t("kb.shot.remove")} onClick={() => onClear(slotKey)}>
              <ImageOff className="size-3.5" />
            </IconBtn>
          </span>
        ) : null}
      </figcaption>
    </figure>
  );
}
